// frontend/src/app/components/projects/project-corpus/dialogs/openalex-form.component.ts
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Output, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ProjectCorpusService } from '../../../../project-corpus/domain/project-corpus.service';
import { ToastService } from '../../../../services/toast.service';
import { ImportSizeEstimate } from '../../../../project-corpus/domain/types';
import {
  WORK_TYPE_COMMON,
  WORK_TYPE_ALL,
  SOURCE_TYPE_COMMON,
  SOURCE_TYPE_ALL,
  TypeOption,
} from '../../../../project-corpus/domain/openalex-types';
import { OpenAlexPreviewComponent } from './openalex-preview.component';

type Mode = 'querying' | 'previewing';

/**
 * OpenAlex query form rendered inside the Add-papers wizard.
 *
 * Two-step flow controlled by an internal `mode` signal:
 *  - `querying` - query input + type-filter checkboxes + Preview button.
 *  - `previewing` - renders `<app-openalex-preview>` with the response;
 *    `Back` returns to `querying`; `Confirm` creates the import job.
 *
 * Responsive behaviour:
 *  - The Preview submit button is full-width on mobile, inline-end on
 *    desktop (`w-full sm:w-auto sm:self-end`).
 */
@Component({
  selector: 'app-openalex-form',
  standalone: true,
  imports: [CommonModule, FormsModule, OpenAlexPreviewComponent],
  templateUrl: './openalex-form.component.html',
  styleUrls: ['./openalex-form.component.scss'],
})
export class OpenAlexFormComponent {
  private readonly service = inject(ProjectCorpusService);
  private readonly toast = inject(ToastService);

  readonly mode = signal<Mode>('querying');
  readonly query = signal('');

  readonly workTypes = signal<string[]>(['article']);
  readonly sourceTypes = signal<string[]>(['journal', 'conference']);
  readonly showAllTypes = signal(false);
  readonly yearFrom = signal<number | null>(null);
  readonly yearTo = signal<number | null>(null);
  readonly language = signal<string[]>([]);
  readonly customLanguageInput = signal('');
  readonly languageOptions: { value: string; label: string }[] = [
    { value: 'en', label: 'English' },
    { value: 'de', label: 'German' },
    { value: 'fr', label: 'French' },
    { value: 'es', label: 'Spanish' },
    { value: 'zh', label: 'Chinese' },
    { value: 'ja', label: 'Japanese' },
    { value: 'ru', label: 'Russian' },
    { value: 'pt', label: 'Portuguese' },
    { value: 'it', label: 'Italian' },
    { value: 'ar', label: 'Arabic' },
  ];

  readonly customLanguages = computed(() => {
    const preset = new Set(this.languageOptions.map((o) => o.value));
    return this.language().filter((code) => !preset.has(code));
  });

  readonly previewLoading = signal(false);
  readonly previewError = signal<string | null>(null);
  readonly preview = signal<ImportSizeEstimate | null>(null);

  readonly submitting = signal(false);
  readonly submitError = signal<string | null>(null);

  /** Emitted after a successful ingest, so the wizard parent can close. */
  @Output() done = new EventEmitter<void>();

  get workTypeOptions(): readonly TypeOption[] {
    return this.showAllTypes() ? WORK_TYPE_ALL : WORK_TYPE_COMMON;
  }

  get sourceTypeOptions(): readonly TypeOption[] {
    return this.showAllTypes() ? SOURCE_TYPE_ALL : SOURCE_TYPE_COMMON;
  }

  setQuery(value: string): void {
    this.query.set(value);
  }

  isQueryValid(): boolean {
    return this.query().trim().length > 0;
  }

  toggleWorkType(value: string): void {
    const current = this.workTypes();
    const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
    this.workTypes.set(next);
    this.resetPreview();
  }

  toggleSourceType(value: string): void {
    const current = this.sourceTypes();
    const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
    this.sourceTypes.set(next);
    this.resetPreview();
  }

  toggleShowAllTypes(): void {
    this.showAllTypes.update((v) => !v);
  }

  setYearFrom(raw: string): void {
    this.yearFrom.set(raw ? parseInt(raw, 10) : null);
    this.resetPreview();
  }

  setYearTo(raw: string): void {
    this.yearTo.set(raw ? parseInt(raw, 10) : null);
    this.resetPreview();
  }

  toggleLanguage(value: string): void {
    const current = this.language();
    const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
    this.language.set(next);
    this.resetPreview();
  }

  removeLanguage(code: string): void {
    this.language.update((current) => current.filter((v) => v !== code));
    this.resetPreview();
  }

  addCustomLanguage(raw: string): void {
    this.customLanguageInput.set('');
    if (!raw.trim()) return;
    const codes = raw
      .split(/[,\s]+/)
      .map((s) => s.trim().toLowerCase())
      .filter((s) => s.length >= 2 && s.length <= 3);
    if (codes.length === 0) return;
    const current = this.language();
    const toAdd = codes.filter((c) => !current.includes(c));
    if (toAdd.length > 0) {
      this.language.set([...current, ...toAdd]);
      this.resetPreview();
    }
  }

  private resetPreview(): void {
    this.mode.set('querying');
    this.preview.set(null);
  }

  loadPreview(): void {
    if (!this.isQueryValid() || this.previewLoading()) return;
    this.previewLoading.set(true);
    this.previewError.set(null);
    const q = this.query().trim();
    this.service
      .previewOpenAlex(
        q,
        this.workTypes(),
        this.sourceTypes(),
        this.yearFrom() ?? undefined,
        this.yearTo() ?? undefined,
        this.language(),
      )
      .subscribe({
        next: (estimate) => {
          this.preview.set(estimate);
          this.mode.set('previewing');
          this.previewLoading.set(false);
        },
        error: (err: unknown) => {
          this.previewError.set(this.toMessage(err));
          this.previewLoading.set(false);
        },
      });
  }

  backToQuery(): void {
    this.mode.set('querying');
    this.preview.set(null);
  }

  confirmIngest(): void {
    if (this.submitting()) return;
    const q = this.query().trim();
    if (!q) return;
    this.submitting.set(true);
    this.submitError.set(null);
    this.service
      .createImportJob({
        source: 'OPENALEX',
        query: q,
        workTypes: this.workTypes(),
        sourceTypes: this.sourceTypes(),
        yearFrom: this.yearFrom() ?? undefined,
        yearTo: this.yearTo() ?? undefined,
        language: this.language(),
      })
      .subscribe({
        next: () => {
          this.submitting.set(false);
          this.toast.success(
            'OpenAlex import started',
            'Your OpenAlex search results are being imported.',
          );
          this.done.emit();
        },
        error: (err: unknown) => {
          this.submitError.set(this.toMessage(err));
          this.submitting.set(false);
        },
      });
  }

  private toMessage(err: unknown): string {
    if (err instanceof Error) return err.message;
    return String(err);
  }
}
